import React from 'react';
import { PHASE, UI } from '../constants/colors';
import './BasalTemperatureChart.css';

/**
 * Courbe de température basale du cycle en cours.
 *
 * Sœur de `CycleEvolutionChart` (même SVG à la main, même grille, même lissage) avec
 * trois différences imposées par la donnée :
 *
 * - l'échelle Y est **zoomée sur les valeurs**, pas ancrée à zéro, et graduée à pas rond.
 *   Le signal cherché est un plateau de +0,3 à +0,5 °C : sur une échelle partant de 0 il
 *   serait invisible, et sur des graduations au pas quelconque il se lirait de travers ;
 * - l'axe X porte le **jour de cycle**, pas le rang du relevé. Une courbe de température
 *   se lit par la position du décalage thermique dans le cycle : espacer les points
 *   régulièrement ferait ressembler un trou de quatre jours à un jour, décalerait le
 *   plateau et désalignerait le repère d'ovulation ;
 * - un relevé signalé perturbé (nuit blanche, fièvre, alcool) est affiché en cercle creux
 *   et **exclu du tracé**. Le moteur l'ignore pour la règle 3/6 : le montrer dans la
 *   courbe laisserait croire qu'il compte, le masquer laisserait croire qu'il est perdu.
 *
 * Le cycle entier tient dans la carte : le glissement au doigt est réservé au passage
 * d'un cycle à l'autre (page Cycle). La courbe défilait auparavant au-delà de 12 jours,
 * et les deux gestes se seraient disputé le même mouvement.
 *
 * Les données viennent telles quelles de `GET /cycles/sportive/{id}/temperatures` : la
 * conversion des écarts en °C et la ligne de base — validée ou provisoire — sont
 * calculées par le moteur, jamais ici.
 */
const BasalTemperatureChart = ({ series }) => {
  const points = series?.points ?? [];

  if (points.length === 0) {
    return (
      <div className="bbt-chart__empty">
        <p>Aucune température relevée sur ce cycle</p>
      </div>
    );
  }

  const width = 320;
  const height = 170;
  const padding = { top: 12, right: 10, bottom: 30, left: 38 };
  // Marge intérieure du tracé : contre l'axe, le premier point et son libellé « J1 »
  // seraient coupés en deux.
  const inset = 8;
  const chartHeight = height - padding.top - padding.bottom;
  // Largeur du tracé, en unités du viewBox. Les deux SVG (axe et tracé) sont dimensionnés
  // en proportion de la largeur de base : textes et points gardent la même échelle.
  const plotUnits = width - padding.left;
  const chartWidth = plotUnits - inset - padding.right;

  const {
    coverline,
    coverline_provisional: coverlineProvisional,
    ovulation_date: ovulationDate,
    ovulation_confirmed: confirmed,
  } = series;

  // Domaine : les relevés et la coverline si elle existe, avec un peu de marge pour que
  // les points ne collent pas aux bords.
  const values = [...points.map((p) => p.value), ...(coverline != null ? [coverline] : [])];
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);

  // Graduations à pas « rond ». Découper l'amplitude en quatre donnait un pas quelconque
  // — 1,4 °C / 4 = 0,35 — et des graduations posées à 36,25 mais **étiquetées 36,3** par
  // l'arrondi d'affichage. Sur ce graphique l'écart en jeu est le dixième de degré, et
  // la coverline se lit à l'œil par rapport à ces lignes : un libellé faux d'un demi-
  // dixième fausse la lecture. En calant les bornes sur des multiples du pas, chaque
  // libellé désigne exactement la ligne qu'il porte.
  //
  // Tout se calcule en **centièmes de degré**, en entiers : en flottants, 36,1 + 0,2 ne
  // vaut pas 36,3, et les libellés repartiraient en 36.300000000000004.
  const PAS_CANDIDATS = [10, 20, 50, 100];   // 0,1 — 0,2 — 0,5 — 1 °C
  const MAX_INTERVALLES = 6;
  const basC = Math.round((rawMin - 0.05) * 100);
  const hautC = Math.round((rawMax + 0.05) * 100);
  const pasC = PAS_CANDIDATS.find(
    (p) => Math.ceil(hautC / p) - Math.floor(basC / p) <= MAX_INTERVALLES
  ) ?? PAS_CANDIDATS[PAS_CANDIDATS.length - 1];

  const minC = Math.floor(basC / pasC) * pasC;
  // Une série plate (un seul relevé, ou plusieurs identiques) donnerait un domaine nul et
  // une division par zéro : au moins trois intervalles, ajoutés par multiples entiers du
  // pas pour que les graduations restent rondes.
  const maxC = Math.max(Math.ceil(hautC / pasC) * pasC, minC + pasC * 3);
  const minY = minC / 100;
  const maxY = maxC / 100;

  const yTicks = [];
  for (let c = minC; c <= maxC; c += pasC) yTicks.push(c / 100);

  const getY = (value) => chartHeight - ((value - minY) / (maxY - minY)) * chartHeight;

  // Axe X en jours de cycle. Le domaine part de J1 et non du premier relevé : commencer
  // au premier jour mesuré masquerait qu'on a démarré la courbe en retard, alors que
  // c'est justement ce qui la rend inexploitable pour dater une ovulation.
  const cycleDays = points.map((p) => p.cycle_day);
  const dayMax = Math.max(...cycleDays);
  // `cycle_day` vaut 0 quand aucun cycle n'est identifié (cf. cycle_router) : le domaine
  // s'effondrerait et tous les points se superposeraient. On retombe alors sur le rang.
  const parJourDeCycle = dayMax > 0;
  const dayMin = parJourDeCycle ? 1 : 0;
  const span = Math.max(parJourDeCycle ? dayMax - dayMin : points.length - 1, 1);


  const getX = (point, index) =>
    ((parJourDeCycle ? point.cycle_day - dayMin : index) / span) * chartWidth;

  // Le tracé ne relie que les mesures exploitables ; les points perturbés restent
  // affichés isolément, à leur place sur l'axe.
  const placed = points.map((p, i) => ({ ...p, x: getX(p, i), y: getY(p.value) }));
  const plotted = placed.filter((p) => !p.disturbed);

  let path = '';
  if (plotted.length > 1) {
    const tension = 0.3;
    path = `M ${plotted[0].x},${plotted[0].y}`;
    for (let i = 1; i < plotted.length; i++) {
      const prev2 = plotted[i - 2] || plotted[i - 1];
      const prev1 = plotted[i - 1];
      const curr = plotted[i];
      const next = plotted[i + 1] || curr;

      const cp1x = prev1.x + (curr.x - prev2.x) * tension;
      const cp1y = prev1.y + (curr.y - prev2.y) * tension;
      const cp2x = curr.x - (next.x - prev1.x) * tension;
      const cp2y = curr.y - (next.y - prev1.y) * tension;

      path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${curr.x},${curr.y}`;
    }
  }

  const ovulationPoint = ovulationDate
    ? placed.find((p) => p.date === ovulationDate)
    : undefined;

  // Au-delà d'une dizaine de libellés sur la largeur, ils deviennent illisibles.
  const labelStep = Math.ceil(points.length / 8);

  const coverlineColor = PHASE.luteale;
  const coverlineLabel = coverlineProvisional ? 'ligne de base provisoire' : 'ligne de base';

  return (
    <div className="bbt-chart">
      {confirmed && (
        <div className="bbt-chart__badge">Ovulation confirmée</div>
      )}

      <div className="bbt-chart__frame">
        {/* Axe des températures, dans son propre SVG à gauche du tracé. */}
        <svg
          viewBox={`0 0 ${padding.left} ${height}`}
          className="bbt-chart__axis"
          style={{ width: `${(padding.left / width) * 100}%` }}
          aria-hidden="true"
        >
          {yTicks.map((tick) => (
            <text
              key={tick}
              x={padding.left - 8}
              y={padding.top + getY(tick)}
              textAnchor="end"
              alignmentBaseline="middle"
              fontSize="10"
              fill={UI.textAxis}
            >
              {tick.toFixed(1)}
            </text>
          ))}
        </svg>

        <div className="bbt-chart__plot" style={{ width: `${(plotUnits / width) * 100}%` }}>
          <svg viewBox={`0 0 ${plotUnits} ${height}`} className="bbt-chart__svg">
            <g transform={`translate(${inset}, ${padding.top})`}>
              {/* Grille horizontale */}
              {yTicks.map((tick) => (
                <line
                  key={tick}
                  x1={0}
                  y1={getY(tick)}
                  x2={chartWidth}
                  y2={getY(tick)}
                  stroke="var(--color-grid)"
                  strokeWidth="1"
                  strokeDasharray="2,2"
                />
              ))}

              {/* Ligne de base : le seuil que les 3 hautes doivent dépasser. Provisoire
                  (trait plus léger) tant que le plateau n'est pas validé. */}
              {coverline != null && (
                <line
                  x1={0}
                  y1={getY(coverline)}
                  x2={chartWidth}
                  y2={getY(coverline)}
                  stroke={coverlineColor}
                  strokeWidth="1.5"
                  strokeDasharray={coverlineProvisional ? '2,3' : '5,3'}
                  strokeOpacity={coverlineProvisional ? 0.6 : 1}
                />
              )}

              {/* Repère du jour d'ovulation */}
              {ovulationPoint && (
                <line
                  x1={ovulationPoint.x}
                  y1={0}
                  x2={ovulationPoint.x}
                  y2={chartHeight}
                  stroke={PHASE.ovulation}
                  strokeWidth="1.5"
                  strokeDasharray="3,3"
                />
              )}

              {path && (
                <path
                  d={path}
                  fill="none"
                  stroke={PHASE.primary}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}

              {placed.map((p) => (
                <circle
                  key={p.date}
                  cx={p.x}
                  cy={p.y}
                  r="3"
                  fill={p.disturbed ? 'var(--surface)' : PHASE.primary}
                  stroke={p.disturbed ? UI.textAxis : 'none'}
                  strokeWidth={p.disturbed ? 1.5 : 0}
                >
                  <title>
                    {`J${p.cycle_day} — ${p.value.toFixed(2)} °C`}
                    {p.is_delta ? ' (saisie en écart)' : ''}
                    {p.disturbed ? ' — perturbée, hors calcul' : ''}
                  </title>
                </circle>
              ))}

              {/* Axe des jours de cycle */}
              {placed.map((p, i) => (
                i % labelStep === 0 ? (
                  <text
                    key={p.date}
                    x={p.x}
                    y={chartHeight + 15}
                    textAnchor="middle"
                    fontSize="10"
                    fill={UI.textAxis}
                  >
                    {`J${p.cycle_day}`}
                  </text>
                ) : null
              ))}
            </g>
          </svg>
        </div>

        {/* Libellé de la ligne de base, posé sur le cadre en HTML : il garde sa taille
            de texte quelle que soit l'échelle du SVG. */}
        {coverline != null && (
          <span
            className="bbt-chart__coverline-label"
            style={{
              top: `${((padding.top + getY(coverline)) / height) * 100}%`,
              left: `${((padding.left + inset) / width) * 100}%`,
              color: coverlineColor,
            }}
          >
            {coverlineLabel}
          </span>
        )}
      </div>

      <div className="bbt-chart__legend">
        <div className="bbt-chart__legend-item">
          <span className="bbt-chart__legend-dot" style={{ background: PHASE.primary }} />
          <span>Température</span>
        </div>
        {coverline != null && (
          <div className="bbt-chart__legend-item">
            <span
              className="bbt-chart__legend-dash"
              style={{ background: coverlineColor, opacity: coverlineProvisional ? 0.6 : 1 }}
            />
            <span>{coverlineProvisional ? 'Ligne de base provisoire' : 'Ligne de base'}</span>
          </div>
        )}
        {ovulationPoint && (
          <div className="bbt-chart__legend-item">
            <span className="bbt-chart__legend-dash" style={{ background: PHASE.ovulation }} />
            <span>Ovulation</span>
          </div>
        )}
        {points.some((p) => p.disturbed) && (
          <div className="bbt-chart__legend-item">
            <span className="bbt-chart__legend-dot bbt-chart__legend-dot--hollow" />
            <span>Perturbée</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default BasalTemperatureChart;
